import { deceasedFullName } from '@shared/api/fevent/deceasedName.util';
import { BugoData } from '@shared/api/bugo/bugo.interface';
import { formatFeventDateString } from '@shared/utils/formatDate';
import { getReverseType } from '@shared/utils/memberUtils';

const bugoTermMessage = (obituaryTerm: string): string => {
  return (
    (obituaryTerm ? obituaryTerm : '별세') + '하셨기에 아래와 같이 부고를 전해 드립니다.'
  );
};

export const bugoLinkUrl = function (bugo: BugoData) {
  return `${process.env.REACT_APP_WEB_BASE_URL}/bugo/${bugo._id}`;
};

//* 관리자가 저장한 SMS 전문이 있으면 그대로 쓴다(스냅샷). 카카오 공유(bugoKakaoShareContent)에는 적용하지 않는다.
export const bugoMessageContentString = function (bugo: BugoData) {
  if (bugo.shareMessageCustom?.trim()) return bugo.shareMessageCustom;
  const member = bugo.member;

  const reverseType = getReverseType(member.relationType, bugo.fevent.deceasedInfo.sex);

  const shareTopString = member.shareDisplayCustom?.topString;
  const shareBottomString = member.shareDisplayCustom?.bottomString;

  const bottom = shareBottomString
    ? shareBottomString
    : `${member.relationType.replace(' ', '')} ${member.fullName} 배상`;

  const header = shareTopString
    ? `${shareTopString}\n`
    : reverseType
    ? `${member.fullName} ${reverseType}상\n`
    : '';

  const contentString = '';
  const fevent = bugo.fevent;

  const deceasedName = fevent.deceasedInfo?.shareDisplayName
    ? fevent.deceasedInfo.shareDisplayName
    : `故 ${deceasedFullName(fevent.deceasedInfo)}님`;

  const accountExpose = bugo.accountExpose ?? fevent.accountExpose ?? false;
  //* 조의금 사절이면 계좌 노출 설정과 무관하게 SMS에서도 계좌를 뺀다
  const hideCondolenceMoney = fevent.hardCustomConfig?.hideCondolenceMoney === true;
  let bankInfo = '';
  if (accountExpose && !hideCondolenceMoney) {
    const cashAccount = member.cashAccount;
    if (
      cashAccount &&
      typeof cashAccount === 'object' &&
      cashAccount.isVerified &&
      cashAccount.account
    ) {
      bankInfo =
        '마음 전할 곳\n' +
        cashAccount.bank +
        ' ' +
        cashAccount.account +
        '\n' +
        '예금주 ' +
        cashAccount.name +
        '\n\n';
    }
  }

  const ret = contentString
    .concat(
      '<訃告>\n',
      header,
      `${deceasedName}께서 ${formatFeventDateString(
        fevent.deceasedInfo.death,
        false,
      )} ${bugoTermMessage(
        fevent.deceasedInfo.bugoTerm ? fevent.deceasedInfo.bugoTerm : '별세',
      )}\n\n`,

      `◆故 ${deceasedFullName(fevent.deceasedInfo)}님 부고◆\n`,
      `${bugoLinkUrl(bugo)}\n\n`,
      (bugo.mournerText || fevent.mournerText) &&
        (bugo.mournerText ? `${bugo.mournerText}\n\n` : `${fevent.mournerText}\n\n`),

      '황망한 마음에 일일이 연락드리지 못함을 널리 혜량해 주시길 바랍니다.\n\n',
      bankInfo,
      bottom,
    )
    .replace('  ', ' ');

  return ret;
};

export interface BugoKakaoShareContent {
  header: string;
  //* 커스텀 템플릿(<訃告> ${header}\n${content}) 본문 — 기존 템플릿은 이 값을 안 쓴다
  content: string;
  deathDate: string;
  deceasedName: string;
  bugoTerm: string;
  bugoId: string;
  //* fevent.kakaoShareTemplateId (없으면 버튼이 빌드 기본 템플릿을 쓴다)
  templateId?: number;
}

//* 기존 템플릿 본문 원문과 같게 유지 — 커스텀 content가 없을 때 이 문장이 나간다
export const bugoKakaoDefaultContent = (
  args: Pick<BugoKakaoShareContent, 'deceasedName' | 'deathDate' | 'bugoTerm'>,
) =>
  `故 ${args.deceasedName}께서 ${args.deathDate} ${args.bugoTerm}하셨기에 알려드립니다....`;

const parseKakaoTemplateId = (value: unknown): number | undefined => {
  const id = Number(value);
  return Number.isFinite(id) && id > 0 ? id : undefined;
};

export const bugoKakaoShareContent = function (bugo: BugoData) {
  const member = bugo.member;
  const fevent = bugo.fevent;
  const reverseType = getReverseType(member.relationType, fevent.deceasedInfo.sex);
  const funeralHomeName = bugo.fevent.funeralHomeInfo.name;
  const shareTopString = member.shareDisplayCustom?.topString;

  const deceasedName = fevent.deceasedInfo?.shareDisplayName
    ? fevent.deceasedInfo.shareDisplayName
    : deceasedFullName(fevent.deceasedInfo);

  const defaultHeader = shareTopString
    ? shareTopString
    : reverseType
    ? `${member.fullName} ${reverseType}상\n${funeralHomeName}`
    : `${funeralHomeName}`;
  const deathDate = formatFeventDateString(fevent.deceasedInfo.death, false);
  const bugoTerm = fevent.deceasedInfo.bugoTerm ? fevent.deceasedInfo.bugoTerm : '별세';

  //* 관리자 커스텀(bugo.kakaoShare*Custom)이 있으면 그 값, 없으면 기존과 같은 값
  return {
    header: bugo.kakaoShareHeaderCustom?.trim()
      ? bugo.kakaoShareHeaderCustom
      : defaultHeader,
    content: bugo.kakaoShareContentCustom?.trim()
      ? bugo.kakaoShareContentCustom
      : bugoKakaoDefaultContent({ deceasedName, deathDate, bugoTerm }),
    deathDate,
    deceasedName,
    bugoTerm,
    bugoId: bugo._id,
    templateId: parseKakaoTemplateId(fevent.kakaoShareTemplateId),
  };
};
